import { useCallback, useId, useState, type CSSProperties, type JSX } from 'react'
import { api, ApiError, NOTE_TAGS, type NoteTag } from '../api.js'
import { asWords } from './Pill.js'
import { T } from '../tokens.js'

/**
 * Writing a note (F-17, R-27) — the write half Plan 4 deliberately left out.
 *
 * ## What this component does not decide
 *
 * **Not the author, and not the timestamp.** `POST /api/accounts/:id/notes`
 * takes `{ body, tag? }` and nothing else; `authorId` comes from the session
 * and `createdAt` from the server's clock, and an unrecognised field is a 400
 * rather than something quietly dropped. So there is no author control here and
 * no date control, and the line under the composer says so in words rather than
 * leaving a rep to wonder what got recorded.
 *
 * The mock puts `M. Reyes · stamps 3:14pm` there instead. That is a claim this
 * component cannot make honestly: it does not know who is signed in — the
 * session cookie is `HttpOnly` and the app bar is the thing that knows — and it
 * does not know the instant the server will stamp. A wrong name beside a
 * permanent entry is worse than no name.
 *
 * **Not the tag vocabulary.** `NOTE_TAGS` is a copy of `config.noteTags` and
 * `api.ts` says why it is allowed to be one: the server resolves the real
 * vocabulary at the note's own `createdAt` and answers 400 to anything else, so
 * a stale copy fails at the moment Save is pressed rather than writing a value
 * into the column R-29's set retrieval would never find again.
 *
 * **Not the labels.** `asWords('curtailment_constraint')` is
 * `Curtailment constraint`. No button a rep uses is labelled with a storage
 * identifier, and the alternative — a hand-written label table — is one more
 * thing to go stale the first time the configuration moves.
 *
 * ## The blank draft is refused here rather than at the server
 *
 * `createNote` rejects an empty body, and its message is
 * `body: must be a non-empty string` — developer-facing text, on the screen of
 * a rep standing in a warehouse. So the refusal is local and says something a
 * person can act on. This is the one thing this component decides that the
 * server also decides, and the duplication is deliberate and one-directional:
 * anything this check lets through, the server still judges.
 */

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const chip: CSSProperties = {
  padding: '5px 12px',
  fontFamily: T.fontFamily,
  fontSize: 11,
  fontWeight: T.weight.bold,
  letterSpacing: '0.04em',
  borderRadius: T.radius.pill,
  cursor: 'pointer',
}

const saveButton: CSSProperties = {
  border: `1.5px solid ${T.color.navy}`,
  background: T.color.navy,
  color: '#fff',
  padding: '10px 14px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
}

/** Verbatim from the mock. */
export const COMPOSER_PLACEHOLDER = 'What the customer said, what you promised, what to check…'

/** Not the server's `body: must be a non-empty string`. See the docblock. */
export const EMPTY_DRAFT = 'A note needs something in it before it can be saved.'

/** A save that did not happen, said as such. Nothing was written. */
export const SAVE_FAILED = 'That note was not saved. Try again.'

/**
 * What was recorded with the note, stated rather than implied. R-27 is
 * "every note carries its author and timestamp", and this is the sentence that
 * makes it something a rep knows rather than something the schema knows.
 */
export const ATTRIBUTION_NOTICE = 'Your name and the time are recorded with every entry.'

type Save =
  | { phase: 'idle' }
  | { phase: 'saving' }
  | { phase: 'saved' }
  | { phase: 'refused'; message: string }

export interface NoteComposerProps {
  accountId: string
  /**
   * Told the new note's id and the tag it was written under.
   *
   * The tag travels because the *feed* owns the characterization prompt — it is
   * the thing that knows whether this account is characterized — and the prompt
   * fires on two specific tags (spec §7.7). Re-reading the tag off the refetched
   * feed would work and would be a second answer to a question this component
   * already has.
   */
  onSaved: (saved: { id: string; tag: NoteTag | null }) => void
}

export function NoteComposer({ accountId, onSaved }: NoteComposerProps): JSX.Element {
  const [draft, setDraft] = useState('')
  const [tag, setTag] = useState<NoteTag | null>(null)
  const [save, setSave] = useState<Save>({ phase: 'idle' })
  // `useId` rather than a literal: nothing stops a second feed appearing on a
  // screen later, and two elements sharing an id make the label point at
  // whichever the browser found first.
  const bodyId = useId()

  const submit = useCallback((event: { preventDefault: () => void }): void => {
    event.preventDefault()
    const body = draft.trim()
    if (body === '') {
      setSave({ phase: 'refused', message: EMPTY_DRAFT })
      return
    }
    setSave({ phase: 'saving' })
    void api.createNote(accountId, body, tag)
      .then((id) => {
        // Cleared only on success. A draft thrown away by a failed save is a
        // rep retyping what the customer said from memory.
        setDraft('')
        setTag(null)
        setSave({ phase: 'saved' })
        onSaved({ id, tag })
      })
      .catch((error: unknown) => {
        setSave({
          phase: 'refused',
          // The server's own words when it sent any — a 400 here is almost
          // always the tag vocabulary having moved, and "recognised tags
          // are …" is the only place this package can honestly learn the
          // current list from.
          message: error instanceof ApiError ? error.detail ?? SAVE_FAILED : SAVE_FAILED,
        })
      })
  }, [accountId, draft, tag, onSaved])

  return (
    <form
      data-testid="note-composer"
      onSubmit={submit}
      style={{ border: `1.5px solid ${T.color.rule}`, padding: 14, marginTop: 12 }}
    >
      <label htmlFor={bodyId} style={{ ...microLabel, display: 'block', marginBottom: 7 }}>
        Note
      </label>
      {/*
        **The three test ids in this file are named away from `NoteFeed`'s on
        purpose.** Both components render into the drill-down's one document, so
        a near-miss id is not a typo that fails — it is a query that finds a real
        element and asserts about the wrong one. This was `note-body` against the
        feed's `note-text`, `note-tags` against its `note-tag`, and `save-note`
        against this file's own `note-saved`.

        `note-draft`: the unsent text. The feed's `note-text` is a note that was
        actually written, and the two are never the same thing.
      */}
      <textarea
        id={bodyId}
        data-testid="note-draft"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value)
          // The confirmation belongs to the note that was saved, not to the
          // one being typed now.
          if (save.phase !== 'idle') setSave({ phase: 'idle' })
        }}
        placeholder={COMPOSER_PLACEHOLDER}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          border: `1px solid ${T.color.hairline}`,
          borderRadius: T.radius.square,
          resize: 'vertical',
          minHeight: 56,
          padding: 8,
          fontFamily: T.fontFamily,
          fontSize: 14.5,
          lineHeight: 1.5,
          color: T.color.ink,
        }}
      />
      <div style={{ height: 1, background: T.color.hairline, margin: '10px 0 12px' }} />
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 16, flexWrap: 'wrap',
        }}
      >
        {/*
          `role="group"` with an `aria-label` rather than a `<fieldset>`: the
          label has to be reachable by a screen reader and must not be
          reachable by `getByLabelText(/note/i)`, which is how the composer's
          own textarea is found on a screen with four other form controls.

          `note-tag-choices`: the control a rep picks a tag WITH. The feed's
          `note-tag` is the pill showing the tag a note already HAS.
        */}
        <div
          role="group"
          aria-label="Tag"
          data-testid="note-tag-choices"
          style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span style={{ ...microLabel, letterSpacing: '0.1em' }}>Tag</span>
          {NOTE_TAGS.map((value) => {
            const selected = tag === value
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                data-tag={value}
                // A second press clears it. "No tag" is a choice a rep makes,
                // and a control that cannot be undone makes the first press a
                // commitment.
                onClick={() => { setTag(selected ? null : value) }}
                style={{
                  ...chip,
                  border: `1.5px solid ${selected ? T.color.navy : T.color.chipOff}`,
                  background: selected ? T.color.navy : '#fff',
                  color: selected ? '#fff' : T.color.body,
                }}
              >
                {asWords(value)}
              </button>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: T.color.micro }}>{ATTRIBUTION_NOTICE}</span>
          {/*
            `note-submit`: the button. `note-saved` below is the confirmation,
            and the two were `save-note` and `note-saved` — the same two words
            either way round, both present once a note has been saved, so a
            query for one would happily return the other.
          */}
          <button type="submit" data-testid="note-submit" disabled={save.phase === 'saving'} style={saveButton}>
            Save note
          </button>
        </div>
      </div>
      {save.phase === 'refused'
        ? (
          <p
            role="alert"
            data-testid="note-not-saved"
            style={{ margin: '10px 0 0', fontSize: 12.5, color: T.color.warnInk }}
          >
            {save.message}
          </p>
        )
        : null}
      {save.phase === 'saved'
        ? (
          <p
            role="status"
            data-testid="note-saved"
            style={{ margin: '10px 0 0', fontSize: 12.5, color: T.color.micro }}
          >
            Saved.
          </p>
        )
        : null}
    </form>
  )
}
